import React, { useEffect, useState } from 'react';

import { Botao, Field, Modal, Select } from '../../../ui';
import { PapelUsuario, SituacaoUsuarioFiltro } from '../../../api/user/userApi';
import { PAPEIS } from '../../../utils/usuarioRegras';

/**
 * Filtros da central de usuários (at-68), no padrão do `ModalFiltrosCarteira` (at-65):
 * trabalha sobre um **rascunho** e só devolve no "Aplicar" — trocar um select não
 * dispara requisição.
 *
 * ⚠️ O default de situação é **ATIVOS**, não "todos": quem administra procura quem
 * trabalha. É por isso que o vazio da lista precisa lembrar que inativos estão fora do
 * recorte — senão "não achei o fulano" vira uma conclusão errada.
 */

export interface FiltrosUsuarios {
  papel: PapelUsuario | '';
  situacao: SituacaoUsuarioFiltro;
}

export const FILTROS_USUARIOS_PADRAO: FiltrosUsuarios = {
  papel: '',
  situacao: 'ATIVOS',
};

/** Conta o que DIVERGE do padrão — o default não é "filtro ativo". */
export const contarFiltrosUsuariosAtivos = (f: FiltrosUsuarios) =>
  (f.papel !== '' ? 1 : 0) + (f.situacao !== FILTROS_USUARIOS_PADRAO.situacao ? 1 : 0);

const OPCOES_SITUACAO: { valor: SituacaoUsuarioFiltro; rotulo: string }[] = [
  { valor: 'ATIVOS', rotulo: 'Somente ativos' },
  { valor: 'INATIVOS', rotulo: 'Somente inativos' },
  { valor: 'TODOS', rotulo: 'Ativos e inativos' },
];

interface ModalFiltrosUsuariosProps {
  aberto: boolean;
  valor: FiltrosUsuarios;
  onAplicar: (filtros: FiltrosUsuarios) => void;
  onFechar: () => void;
}

const ModalFiltrosUsuarios: React.FC<ModalFiltrosUsuariosProps> = ({
  aberto,
  valor,
  onAplicar,
  onFechar,
}) => {
  const [rascunho, setRascunho] = useState<FiltrosUsuarios>(valor);

  useEffect(() => {
    if (aberto) setRascunho(valor);
  }, [aberto, valor]);

  const aplicar = () => {
    onAplicar(rascunho);
    onFechar();
  };

  return (
    <Modal
      aberto={aberto}
      titulo="Filtros"
      descricao="O recorte vale para a lista; os contadores do topo continuam gerais."
      tamanho="sm"
      alinharRodape="direita"
      aoFechar={onFechar}
      rodape={
        <div className="tw-flex tw-flex-wrap tw-gap-3">
          <Botao variante="secundario" onClick={() => setRascunho(FILTROS_USUARIOS_PADRAO)}>
            Limpar
          </Botao>
          <Botao onClick={aplicar}>Aplicar</Botao>
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Field rotulo="Papel">
          <Select
            value={rascunho.papel}
            placeholder="Todos os papéis"
            opcoes={PAPEIS.map(p => ({ valor: p.valor, rotulo: p.rotulo }))}
            onChange={e =>
              setRascunho(r => ({ ...r, papel: e.target.value as PapelUsuario | '' }))
            }
          />
        </Field>

        <Field rotulo="Situação">
          <Select
            value={rascunho.situacao}
            /* Sem placeholder: "Ativos e inativos" JÁ é a opção de "sem recorte", e uma
               opção vazia selecionável gravaria '' e faria o contador mentir. */
            placeholder=""
            opcoes={OPCOES_SITUACAO}
            onChange={e =>
              setRascunho(r => ({ ...r, situacao: e.target.value as SituacaoUsuarioFiltro }))
            }
          />
        </Field>
      </div>
    </Modal>
  );
};

export default ModalFiltrosUsuarios;
